import { useState } from 'react';
import { Plus, Minus, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import Slider from '../ui/Slider';
import SliderValue from '../ui/SliderValue';
import Disclaimer from '../ui/Disclaimer';
import DisclaimerContent from '../../utils/disclaimerContent';
import { useFirstStepDisclaimer } from '../../hooks/useFirstStepDisclaimer';

type AnnualIncomeFormProps = {
  formData: FormData;
  onSubmit: (data: Partial<FormData>) => void;
};

export default function AnnualIncomeForm({ formData, onSubmit }: AnnualIncomeFormProps) {
  const { t } = useTranslation('wizard');
  const ANNUAL_INCOME_DISPLAY_DEFAULT = 50000;
  const [income, setIncome] = useState(formData.annualIncome ?? ANNUAL_INCOME_DISPLAY_DEFAULT);
  const { shouldShow: showDisclaimer } = useFirstStepDisclaimer();
  const minIncome = 20000;
  const maxIncome = 250000;
  const step = 5000;

  const handleSliderChange = (value: number) => {
    setIncome(value);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleSubmit = () => {
    onSubmit({ annualIncome: income });
  };

  const handleIncrement = () => {
    const newValue = Math.min((income || 0) + step, maxIncome);
    handleSliderChange(newValue);
  };

  const handleDecrement = () => {
    const newValue = Math.max((income || 0) - step, minIncome);
    handleSliderChange(newValue);
  };

  return (
    <div id="annual-income-form" className="space-y-8">
      <div className="text-center">
        <Title 
          id="annual-income-title"
          name="annual-income-title"
          as="h1" 
          className="mb-2"
        >
          {t('annualIncome.title')}
        </Title>
        <Description 
          id="annual-income-description"
          name="annual-income-description"
          size="lg"
        >
          {t('annualIncome.description')}
          <span className="ml-2 inline-flex items-center relative group">
            <Info className="w-5 h-5 text-gray-600 cursor-pointer" />
            <span className="absolute left-0 transform -translate-x-full mt-2 w-64 p-2 bg-gray-700 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
              {t('annualIncome.tooltip')}
            </span>
          </span>
        </Description>
      </div>

      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <button
            id="annual-income-decrease"
            name="annual-income-decrease"
            type="button"
            onClick={handleDecrement}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            aria-label={t('annualIncome.decreaseAria')}
          >
            <Minus className="w-5 h-5 text-gray-600" />
          </button>
          
          <SliderValue
            id="annual-income-value"
            name="annual-income-value"
            value={income || 0}
            min={minIncome}
            max={maxIncome}
            formatValue={formatCurrency}
          />
          
          <button
            id="annual-income-increase"
            name="annual-income-increase"
            type="button"
            onClick={handleIncrement}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            aria-label={t('annualIncome.increaseAria')}
          >
            <Plus className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <Slider
          id="annual-income-slider"
          name="annual-income-slider"
          value={income || 0}
          min={minIncome}
          max={maxIncome}
          step={step}
          onChange={handleSliderChange}
          formatValue={formatCurrency}
          showLimits
        />

        <Button
          id="annual-income-submit"
          name="annual-income-submit"
          onClick={handleSubmit}
          fullWidth
          size="lg"
        >
          {t('annualIncome.submit')}
        </Button>

        <Disclaimer
          id="annual-income-disclaimer"
          name="annual-income-disclaimer"
        >
          {t('annualIncome.disclaimer')}
        </Disclaimer>

        {showDisclaimer && (
          <Disclaimer
            id="annual-income-general-disclaimer"
            name="annual-income-general-disclaimer"
          >
            <DisclaimerContent />
          </Disclaimer>
        )}
      </div>
    </div>
  );
}